import { useState, useEffect, useCallback } from "react";
import type { BrandAiTrendPureDict } from "@/services/api/brandAiMetricsTrend";
import { modelsApi, type AIModel } from "@/services/api/models";

export interface PlatformVisibilitySeries {
  name: string;
  values: number[];
  color?: string;
  isCurrentBrand?: boolean;
}

export interface PlatformVisibilityData {
  xLabels: string[];
  series: PlatformVisibilitySeries[];
  platforms: string[]; // AI 平台列表
}

interface UsePlatformVisibilityProps {
  brandAiMetricsTrend?: BrandAiTrendPureDict;
  metricType?: string;
}

export const usePlatformVisibility = ({
  brandAiMetricsTrend,
  metricType = "mentionRate",
}: UsePlatformVisibilityProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<PlatformVisibilityData | null>(
    null
  );
  const [modelNameMap, setModelNameMap] = useState<Record<string, string>>({});

  // 预取模型名称映射，用于把 aiId 转成模型展示名称
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const models: AIModel[] = await modelsApi.getModelsList();
        if (!mounted) return;
        const map: Record<string, string> = {};
        for (const m of models) {
          if (m?.id) map[m.id] = m.name || m.ename || m.id;
        }
        setModelNameMap(map);
      } catch {
        // 忽略错误，使用 aiId 作为回退
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // 处理平台可见性数据
  const processVisibilityData = useCallback(() => {
    //console.log('usePlatformVisibility.processVisibilityData - 处理数据:', { brandAiMetricsTrend, metricType })

    if (!brandAiMetricsTrend || Object.keys(brandAiMetricsTrend).length === 0) {
      //console.log('usePlatformVisibility.processVisibilityData - 无数据，跳过处理')
      setChartData(null);
      setError("无 AI 平台数据");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 处理 AI 平台数据
      const dict = brandAiMetricsTrend;
      const metricDict = dict[metricType] || {};
      const nameMap = (dict as any).__aiNameMap as
        | Record<string, string>
        | undefined;

      // 将 { aiId: series } 转换为 { aiName: series }，仅用于展示
      const byName: Record<string, Array<{ day: string; rate: number }>> = {};
      Object.keys(metricDict).forEach((aiId) => {
        const displayName = nameMap?.[aiId] || modelNameMap[aiId] || aiId;
        byName[displayName] = metricDict[aiId] || [];
      });
      const aiNames = Object.keys(byName);
      if (aiNames.length > 0) {
        const allDates = [
          ...new Set(
            aiNames.flatMap((n) => (byName[n] || []).map((p) => p.day))
          ),
        ].sort();
        const series = aiNames.map((name, index) => {
          const platformSeries = byName[name] || [];
          const values = allDates.map((d) => {
            const found = platformSeries.find((p) => p.day === d);
            return found ? found.rate : 0;
          });
          return {
            name,
            values,
            color: ["#1890ff", "#52c41a", "#faad14", "#f5222d", "#722ed1"][
              index % 5
            ],
            isCurrentBrand: false,
          };
        });
        const transformedData: PlatformVisibilityData = {
          xLabels: allDates,
          series,
          platforms: aiNames,
        };

        console.log(
          "usePlatformVisibility.loadVisibilityData - 转换后数据:",
          transformedData
        );

        setChartData(transformedData);
      } else {
        setError("未获取到 AI 平台数据");
      }
    } catch (err) {
      console.error("Failed to process platform visibility data:", err);
      setError("处理平台可见性数据失败");
    } finally {
      setLoading(false);
    }
  }, [brandAiMetricsTrend, metricType]);

  // 数据变化时重新处理
  useEffect(() => {
    processVisibilityData();
  }, [processVisibilityData]);

  return {
    loading,
    error,
    chartData,
    reload: processVisibilityData,
  };
};
